/**
 * Application bootstrap. Wires URL params → API → state → Rive + Pixi layers,
 * then hands off to the orchestrator. Mirrors the spirit of
 *   apps/amg_sicbo_v2/lib/main.dart
 * but as a single entry point since this build only has one route.
 *
 * The betting UI is rendered by sicbobetting_v2.riv and emits Rive events
 * (placeBetClick / evtClickChips / evtClearClick / evtButtonPlayClick).
 * Chip placement uses the actual pointer-down coordinates (captured here)
 * rather than per-bet scatter rectangles.
 */
import { Api } from './net/api';
import { MultiplayerHub } from './net/multiplayerHub';
import { GameState } from './game/gameState';
import { GameOrchestrator } from './game/gameOrchestrator';
import { loadUrlParams } from './game/urlParams';
import { RiveLayer } from './rive/riveLayer';
import { PixiLayer } from './pixi/pixiLayer';
import { ChipLayer } from './pixi/chipLayer';
import { Hud } from './ui/hud';
import { TableControls } from './ui/controls';
import { GameTimerWidget } from './ui/timerWidget';
import { GameLogWidget } from './ui/gameLogWidget';
import { GameResultPanel } from './ui/gameResultPanel';
import { SoundManager } from './audio/soundManager';
import { SoundToggle } from './ui/soundToggle';
import {
  bindGameSounds,
  CHIP_SELECT_SFX,
  LOSE_SFX,
  SFX_CLIPS,
  WIN_SFX,
} from './audio/soundWiring';
import { PerfHud } from './perf/perfHud';
import { ResponsiveScaler } from './util/responsiveScaler';
import { MULTIPLAYER_HUB_URL, REF_HEIGHT, REF_WIDTH } from './config';
import { debugLog, error as logError } from './util/logger';

declare global {
  interface Window {
    // Defined by the inline loading-splash controller in index.html.
    hideLoadingScreen?: () => void;
  }
}

interface XY {
  x: number;
  y: number;
}

async function bootstrap(): Promise<void> {
  if (!('WebGL2RenderingContext' in window)) {
    showFatal('Your browser does not support WebGL2. Please use a modern browser.');
    return;
  }

  const scaler = new ResponsiveScaler('stage');
  scaler.attach();

  const params = loadUrlParams();
  debugLog('[main] URL params', params);

  const riveMain = document.getElementById('rive-canvas-main') as HTMLCanvasElement;
  const riveTopLayer = document.getElementById('rive-canvas-toplayer') as HTMLCanvasElement;
  const riveBetting = document.getElementById('rive-canvas-betting') as HTMLCanvasElement;
  const pixiCanvas = document.getElementById('pixi-canvas') as HTMLCanvasElement;
  const stage = document.getElementById('stage');
  if (!riveMain || !riveTopLayer || !riveBetting || !pixiCanvas || !stage) {
    showFatal('Stage canvases missing from DOM.');
    return;
  }

  // ---- Pointer-down listener on the stage: captures tap position in
  //      1792×828 logical coords, used by chipLayer.placeChip(). The Rive
  //      betting artboard simultaneously emits a placeBetClick event with
  //      the betID — we combine the two on tryPlaceBet().
  let lastTap: XY | null = null;
  const updateLastTap = (e: PointerEvent): void => {
    const rect = stage.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    const x = ((e.clientX - rect.left) / rect.width) * REF_WIDTH;
    const y = ((e.clientY - rect.top) / rect.height) * REF_HEIGHT;
    lastTap = { x, y };
  };
  stage.addEventListener('pointerdown', updateLastTap, { passive: true });

  const rive = new RiveLayer({
    canvases: {
      main: riveMain,
      topLayer: riveTopLayer,
      betting: riveBetting,
    },
  });
  const pixi = new PixiLayer(pixiCanvas);

  const perfHud = new PerfHud();
  perfHud.start();

  const state = new GameState();

  // ---- SFX (external clips only). The toggle gates our SoundManager; it does
  //      NOT touch the .riv's own isSound/BGM. Audio is unlocked on the first
  //      user gesture so the first programmatic play isn't autoplay-blocked.
  const sound = new SoundManager();
  sound.preload(SFX_CLIPS);
  window.addEventListener('pointerdown', sound.unlock, { once: true });
  window.addEventListener('keydown', sound.unlock, { once: true });
  void new SoundToggle({ onChange: (on) => sound.setEnabled(on) });

  // DOM/state-driven SFX off the GameState chokepoint.
  state.on((next, prev) => {
    const chipSelected = next.selectedChipIndex !== prev.selectedChipIndex;
    const betPlaced = next.bets.total > prev.bets.total;
    if (chipSelected || betPlaced) sound.play(CHIP_SELECT_SFX);

    // Win/lose SFX fire when the DOM result panel appears (showWin/LosePanel
    // flip true). The MainWinLose.riv event route is deferred for later.
    if (next.showWinPanel && !prev.showWinPanel) sound.play(WIN_SFX);
    if (next.showLosePanel && !prev.showLosePanel) sound.play(LOSE_SFX);
  });

  const api = new Api();
  const hub = new MultiplayerHub(MULTIPLAYER_HUB_URL);
  const hud = new Hud();
  const timer = new GameTimerWidget();
  const resultPanel = new GameResultPanel();
  void new GameLogWidget();

  let orchestratorRef: GameOrchestrator | null = null;
  const controls = new TableControls({
    onSelectChip: (i) => state.selectChip(i),
    onClear: () => orchestratorRef?.clearAll(),
    onConfirm: () => void orchestratorRef?.confirm(),
  });

  try {
    await Promise.all([pixi.init(), rive.init()]);
  } catch (e) {
    logError('[main] init failed', e);
    showFatal(`Failed to load assets: ${(e as Error)?.message ?? String(e)}`);
    return;
  }
  state.setRiveReady(true);

  // Ready for this game's Rive-event SFX (EVENT_SFX is empty for now).
  bindGameSounds(rive, sound);

  // Default to mouse-mode (desktop hover effects). Touch input still works.
  rive.setIsMouse(!('ontouchstart' in window));

  const chips = new ChipLayer(pixi.root);

  const orchestrator = new GameOrchestrator({
    state,
    api,
    rive,
    chips,
    hud,
    controls,
    hub,
    timer,
    resultPanel,
    params,
    getLastTap: () => lastTap,
    onInteractive: () => {
      perfHud.markInteractive();
      // Game is signed in, configured and rendering — drop the loading splash.
      window.hideLoadingScreen?.();
    },
  });
  orchestratorRef = orchestrator;

  await orchestrator.start();
}

function showFatal(message: string): void {
  // Drop the loading splash first — it sits above the fatal overlay.
  window.hideLoadingScreen?.();
  const overlay = document.getElementById('fatal-overlay');
  const msg = document.getElementById('fatal-message');
  if (msg) msg.textContent = message;
  if (overlay) overlay.style.display = 'flex';
}

window.addEventListener('error', (e) => logError('[window.error]', e.error ?? e.message));
window.addEventListener('unhandledrejection', (e) =>
  logError('[unhandledrejection]', e.reason),
);

bootstrap().catch((e) => {
  logError('[main] bootstrap crashed', e);
  showFatal(`Bootstrap failed: ${(e as Error)?.message ?? String(e)}`);
});
